import type { ListActionRef, ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import { errorMessage as errorMessageOf, isFormValidationError } from "../../../app/utils/errorMessage";
import { DeleteOutlined,EditOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

import { Button,Form } from "../../../../../../../shared/frontend/ui";
import type { ModalFuncProps } from "../../../../../../../shared/frontend/ui";
import dayjs,{ type Dayjs } from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import type { LocalizedText } from "../../../app/i18n/i18n";
import {
  CYCLE_UNIT_DAILY,
  CYCLE_UNIT_MONTHLY,
  CYCLE_UNIT_ONE_TIME,
  CYCLE_UNIT_WEEKLY,
  CYCLE_UNIT_YEARLY
} from "../../../app/constants/cycleUnit";
import { PRICE_ADJUSTMENT_TYPE_PERCENTAGE, PRICE_ADJUSTMENT_TYPE_SET_PRICE } from "../../../app/constants/priceAdjustmentType";
import { formatDateOnly,formatMoney,formatPercent } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { billingCycleLabel } from "../../../app/utils/billingCycle";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import type {
  ApiContractPackageDto,
  ApiPersonServiceDto,
  ContractPriceAdjustmentRow
} from "./personDetailTypes";
import {
  CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION,
  compareDateOnlyDesc,
  compareTextDesc,
  formatDateOnlyIso
} from "./personDetailUtils";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { personDetailQueryKeys } from "./personDetailQueryKeys";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

/** Wie weit (Jahre) der Zahlungsplan eines Vertrags auf ein Anpassungsdatum geprüft wird. */
const SCHEDULE_LOOKAHEAD_YEARS = 2;
const PERCENT_DISPLAY_DECIMALS = 2;
/** Größte zulässige prozentuale Preissenkung. */
const MAX_PERCENTAGE_REDUCTION = 100;

export type PriceAdjustmentDefinition = {
  id: string;
  name: string;
  adjustmentType: number;
  adjustmentValue: number;
  cycleUnit: number;
  isActive: boolean;
};

export type PriceAdjustmentContractRow = {
  id: string;
  name: string;
  startDate: string;
  endDate?: string | null;
  value?: number | null;
  paymentCycleValue?: number | null;
  paymentCycleUnit?: number | null;
  sourceContractPackageId: string | null;
  isPrimary: boolean;
};

export type ContractPriceAdjustmentFormValues = {
  contractPackageId?: string | null;
  contractId?: string | null;
  priceAdjustmentId?: string | null;
  manualAdjustmentName?: string | null;
  manualAdjustmentType?: number | null;
  manualAdjustmentValue?: number | null;
  manualAdjustmentCycleUnit?: number | null;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  note?: string | null;
};

type UsePersonPriceAdjustmentControllerArgs = {
  api: Pick<ApiClient, "get" | "post" | "put" | "del">;
  personId: string;
  text: (value: LocalizedText) => string;
  units: UnitsSettings;
  priceAdjustments: PriceAdjustmentDefinition[];
  canCustomizePriceAdjustments: boolean;
  message: {
    success(content: React.ReactNode): void;
    error(content: React.ReactNode): void;
  };
  modal: {
    confirm(config: ModalFuncProps): unknown;
  };
  onContributionsChanged: () => void;
  onOpenItemsChanged: () => void;
};

function normalizePriceAdjustmentContracts(list: ApiPersonServiceDto[]): PriceAdjustmentContractRow[] {
  return (list || []).map((x) => ({
    id: String(x.id),
    name: String(x.name || ""),
    startDate: String(x.startDate || ""),
    endDate: x.endDate ?? null,
    value: x.value ?? null,
    paymentCycleValue: x.paymentCycleValue != null ? Number(x.paymentCycleValue) : null,
    paymentCycleUnit: x.paymentCycleUnit != null ? Number(x.paymentCycleUnit) : null,
    sourceContractPackageId:
      x.sourceContractPackageId != null && x.sourceContractPackageId !== ""
        ? String(x.sourceContractPackageId)
        : null,
    isPrimary: Boolean(x.isPrimary)
  }));
}

function normalizePriceAdjustmentPackages(list: ApiContractPackageDto[]): Array<{ id: string; planName: string }> {
  return (list || []).map((p) => ({
    id: String(p.id),
    planName: String(p.planName || "").trim() || String(p.id)
  }));
}

function comparePriceAdjustmentsNewestFirst(a: ContractPriceAdjustmentRow, b: ContractPriceAdjustmentRow): number {
  return (
    compareDateOnlyDesc(a.startDate, b.startDate)
    || compareDateOnlyDesc(a.endDate, b.endDate)
    || compareTextDesc(a.createdAt, b.createdAt)
    || compareTextDesc(a.id, b.id)
  );
}

function getContractPackageSelectId(contract: PriceAdjustmentContractRow | undefined): string | null {
  if (!contract) return null;
  return contract.sourceContractPackageId
    ? String(contract.sourceContractPackageId)
    : CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION;
}

export function getNextPriceAdjustmentPeriodStart(
  contract: { startDate: string; paymentCycleValue?: number | null; paymentCycleUnit?: number | null }
): string | null {
  const start = dayjs(String(contract.startDate).slice(0, ISO_DATE_LENGTH));
  if (!start.isValid()) return null;
  const today = dayjs().startOf("day");
  const cycle = contract.paymentCycleUnit != null ? Number(contract.paymentCycleUnit) : CYCLE_UNIT_MONTHLY;
  const cycleValue = contract.paymentCycleValue != null && Number(contract.paymentCycleValue) > 0 ? Number(contract.paymentCycleValue) : 1;
  if (cycle === CYCLE_UNIT_ONE_TIME) {
    return start.startOf("day").format("YYYY-MM-DD");
  }
  let current = start;
  while (current.isBefore(today)) {
    if (cycle === CYCLE_UNIT_MONTHLY) current = current.add(cycleValue, "month");
    else if (cycle === CYCLE_UNIT_WEEKLY) current = current.add(cycleValue, "week");
    else if (cycle === CYCLE_UNIT_YEARLY) current = current.add(cycleValue, "year");
    else if (cycle === CYCLE_UNIT_DAILY) current = current.add(cycleValue, "day");
    else current = current.add(cycleValue, "month");
  }
  return current.format("YYYY-MM-DD");
}

export function isValidPriceAdjustmentPeriodStart(
  contract: { startDate: string; paymentCycleValue?: number | null; paymentCycleUnit?: number | null },
  date: dayjs.Dayjs
): boolean {
  if (!date || !date.isValid()) return false;
  const start = dayjs(String(contract.startDate).slice(0, ISO_DATE_LENGTH)).startOf("day");
  if (!start.isValid()) return false;
  const cycle = contract.paymentCycleUnit != null ? Number(contract.paymentCycleUnit) : CYCLE_UNIT_MONTHLY;
  const cycleValue = contract.paymentCycleValue != null && Number(contract.paymentCycleValue) > 0 ? Number(contract.paymentCycleValue) : 1;
  if (cycle === CYCLE_UNIT_ONE_TIME) return date.isSame(start, "day");
  let current = start;
  const limit = date.add(SCHEDULE_LOOKAHEAD_YEARS, "year");
  while (!current.isAfter(limit)) {
    if (current.isSame(date, "day")) return true;
    if (current.isAfter(date)) return false;
    if (cycle === CYCLE_UNIT_MONTHLY) current = current.add(cycleValue, "month");
    else if (cycle === CYCLE_UNIT_WEEKLY) current = current.add(cycleValue, "week");
    else if (cycle === CYCLE_UNIT_YEARLY) current = current.add(cycleValue, "year");
    else if (cycle === CYCLE_UNIT_DAILY) current = current.add(cycleValue, "day");
    else current = current.add(cycleValue, "month");
  }
  return false;
}

function normalizePriceAdjustmentCycleUnit(cycleUnit: number | string | null | undefined): number {
  const parsed = Number(cycleUnit);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : CYCLE_UNIT_ONE_TIME;
}

export function usePersonPriceAdjustmentController({
  api,
  personId,
  text,
  units,
  priceAdjustments,
  canCustomizePriceAdjustments,
  message,
  modal,
  onContributionsChanged,
  onOpenItemsChanged
}: UsePersonPriceAdjustmentControllerArgs) {
  const queryClient = useQueryClient();
  const actionRef = React.useRef<ListActionRef>(null);
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [targetsLoading, setTargetsLoading] = React.useState(false);
  const targetsGeneration = React.useRef(0);
  const [edit, setEdit] = React.useState<ContractPriceAdjustmentRow | null>(null);
  const [individualContracts, setIndividualContracts] = React.useState<PriceAdjustmentContractRow[]>([]);
  const [contractPackages, setContractPackages] = React.useState<Array<{ id: string; planName: string }>>([]);
  const [selectedContractPackageId, setSelectedContractPackageId] = React.useState<string | null>(null);
  const [form] = Form.useForm<ContractPriceAdjustmentFormValues>();
  const { dirty, initialize } = useFormDirty(form);

  const financeOperation = useFinanceOperation(personId);
  const reload = React.useCallback(() => {
    actionRef.current?.reload?.();
  }, []);

  const reloadDependents = React.useCallback(() => {
    reload();
    onContributionsChanged();
    onOpenItemsChanged();
  }, [onContributionsChanged, onOpenItemsChanged, reload]);

  const formatAdjustmentValue = React.useCallback(
    (value: number, type: number) => {
      const val = Number(value || 0);
      const resolvedType = Number(type || PRICE_ADJUSTMENT_TYPE_PERCENTAGE);
      // Ein Zielpreis ist kein Auf- oder Abschlag — ein Vorzeichen wäre hier irreführend.
      if (resolvedType === PRICE_ADJUSTMENT_TYPE_SET_PRICE) return formatMoney(val, units);
      const sign = val > 0 ? "+" : "";
      return resolvedType === PRICE_ADJUSTMENT_TYPE_PERCENTAGE
        ? `${sign}${formatPercent(val, units, PERCENT_DISPLAY_DECIMALS)}`
        : `${sign}${formatMoney(val, units)}`;
    },
    [units]
  );

  const applyPriceAdjustmentDefinition = React.useCallback(
    (priceAdjustmentId?: string | null) => {
      if (!priceAdjustmentId) return;
      const definition = priceAdjustments.find((x) => String(x.id) === String(priceAdjustmentId));
      if (!definition) return;
      form.setFieldsValue({
        priceAdjustmentId: String(definition.id),
        manualAdjustmentName: definition.name,
        manualAdjustmentType: Number(definition.adjustmentType),
        manualAdjustmentValue: Number(definition.adjustmentValue),
        manualAdjustmentCycleUnit: normalizePriceAdjustmentCycleUnit(definition.cycleUnit)
      });
    },
    [form, priceAdjustments]
  );

  const loadAdjustmentTargets = React.useCallback(async () => {
    if (!personId) return { contracts: [] as PriceAdjustmentContractRow[], packages: [] as Array<{ id: string; planName: string }> };
    const [contracts, packages] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.person.contractBlocks(personId),
        queryFn: () => api.get<ApiPersonServiceDto[]>(`/api/v1/contracts?personId=${encodeURIComponent(personId)}`), staleTime: 0
      }),
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.person.contractPackages(personId),
        queryFn: () => api.get<ApiContractPackageDto[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`), staleTime: 0
      })
    ]);
    return {
      contracts: normalizePriceAdjustmentContracts(contracts || []),
      packages: normalizePriceAdjustmentPackages(packages || [])
    };
  }, [api, personId, queryClient]);

  const loadTargetsForDrawer = React.useCallback(async () => {
    const generation = ++targetsGeneration.current;
    setTargetsLoading(true);
    try {
      const targets = await loadAdjustmentTargets();
      if (generation !== targetsGeneration.current) return null;
      setIndividualContracts(targets.contracts);
      setContractPackages(targets.packages);
      return targets;
    } catch {
      if (generation === targetsGeneration.current) setOpen(false);
      return null; // API-Client zeigt den konkreten Ladefehler.
    } finally {
      if (generation === targetsGeneration.current) setTargetsLoading(false);
    }
  }, [loadAdjustmentTargets]);

  const invalidateTargetRequest = React.useCallback(() => { targetsGeneration.current++; }, []);
  React.useEffect(() => invalidateTargetRequest, [loadAdjustmentTargets, invalidateTargetRequest]);

  const openEdit = React.useCallback(
    async (row: ContractPriceAdjustmentRow) => {
      const isManual = !row.priceAdjustmentId;
      if (isManual && !canCustomizePriceAdjustments) {
        message.error(text("Manual price adjustment requires customize permission"));
        return;
      }
      setEdit(row);
      form.resetFields();
      setOpen(true);
      const targets = await loadTargetsForDrawer();
      if (!targets) return;
      const contract = targets.contracts.find((x) => x.id === row.contractId);
      const packageId = getContractPackageSelectId(contract);
      setSelectedContractPackageId(packageId);
      initialize({
        contractPackageId: packageId,
        priceAdjustmentId: row.priceAdjustmentId,
        manualAdjustmentName: row.adjustmentNameSnapshot || null,
        manualAdjustmentType: Number(row.adjustmentTypeSnapshot || PRICE_ADJUSTMENT_TYPE_PERCENTAGE),
        manualAdjustmentValue: Number(row.adjustmentValueSnapshot || 0),
        manualAdjustmentCycleUnit: normalizePriceAdjustmentCycleUnit(row.adjustmentCycleUnitSnapshot),
        contractId: row.contractId,
        startDate: row.startDate ? dayjs(String(row.startDate).slice(0, ISO_DATE_LENGTH)) : dayjs(),
        endDate: row.endDate ? dayjs(String(row.endDate).slice(0, ISO_DATE_LENGTH)) : null,
        note: row.note ?? null
      });
      setOpen(true);
    },
    [canCustomizePriceAdjustments, form, initialize, loadTargetsForDrawer, message, text]
  );

  const openCreate = React.useCallback(async () => {
    setEdit(null);
    setSelectedContractPackageId(null);
    form.resetFields();
    initialize({
      contractPackageId: null,
      priceAdjustmentId: null,
      manualAdjustmentName: null,
      manualAdjustmentType: canCustomizePriceAdjustments ? PRICE_ADJUSTMENT_TYPE_PERCENTAGE : null,
      manualAdjustmentValue: null,
      manualAdjustmentCycleUnit: canCustomizePriceAdjustments ? CYCLE_UNIT_ONE_TIME : null,
      contractId: null,
      startDate: null,
      endDate: null,
      note: null
    });
    setOpen(true);
    await loadTargetsForDrawer();
  }, [canCustomizePriceAdjustments, form, initialize, loadTargetsForDrawer]);

  const close = React.useCallback(() => {
    targetsGeneration.current++;
    setTargetsLoading(false);
    setOpen(false);
    setEdit(null);
    setSelectedContractPackageId(null);
    form.resetFields();
  }, [form]);

  const deleteRow = React.useCallback(
    async (row: ContractPriceAdjustmentRow) => {
      const ok = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: text("Delete price adjustment"),
          content: text("Really delete?"),
          okText: text("Delete"),
          okButtonProps: { danger: true },
          cancelText: text("Cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      if (!ok) return;
      try {
        await financeOperation.execute(async (committed) => {
          await api.del(`/api/v1/contract-price-adjustments/${encodeURIComponent(row.id)}`);
          committed();
          message.success(text("Deleted."));
        }, async () => {
          await queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.priceAdjustments(personId) });
          reloadDependents();
        });
      } catch {
        // Central API toast shows error.
      }
    },
    [api, message, modal, personId, queryClient, reloadDependents, text]
  );

  const listConfig = React.useMemo(() => {
    const columnDefinitions: ColumnDefinition[] = [
      { key: "contractName", label: text("Contract") },
      { key: "adjustmentNameSnapshot", label: text("Price adjustment") },
      { key: "adjustmentCycleUnitSnapshot", label: text("Cycle") },
      { key: "startDate", label: text("From") },
      { key: "endDate", label: text("Until") },
      { key: "note", label: text("Note") }
    ];
    const allColumnsMap: Record<string, ListColumn<ContractPriceAdjustmentRow>> = {
      contractName: {
        title: text("Contract"),
        dataIndex: "contractName",
        key: "contractName",
        render: (_, row) => row.contractName || "—"
      },
      adjustmentNameSnapshot: {
        title: text("Price adjustment"),
        dataIndex: "adjustmentNameSnapshot",
        key: "adjustmentNameSnapshot",
        render: (_, row) => {
          const adjustmentText = formatAdjustmentValue(
            Number(row.adjustmentValueSnapshot || 0),
            Number(row.adjustmentTypeSnapshot || PRICE_ADJUSTMENT_TYPE_PERCENTAGE)
          );
          return `${row.adjustmentNameSnapshot || ""} (${adjustmentText})`;
        }
      },
      adjustmentCycleUnitSnapshot: {
        title: text("Cycle"),
        dataIndex: "adjustmentCycleUnitSnapshot",
        key: "adjustmentCycleUnitSnapshot",
        render: (_, row) => billingCycleLabel(normalizePriceAdjustmentCycleUnit(row.adjustmentCycleUnitSnapshot), text)
      },
      startDate: {
        title: text("From"),
        dataIndex: "startDate",
        key: "startDate",
        render: (_, row) => formatDateOnly(String(row.startDate || "").slice(0, ISO_DATE_LENGTH), units)
      },
      endDate: {
        title: text("Until"),
        dataIndex: "endDate",
        key: "endDate",
        render: (_, row) => (row.endDate ? formatDateOnly(String(row.endDate).slice(0, ISO_DATE_LENGTH), units) : "—")
      },
      note: {
        title: text("Note"),
        dataIndex: "note",
        key: "note",
        render: (_, row) => row.note || "—"
      }
    };
    const actionsColumn: ListColumn<ContractPriceAdjustmentRow> = {
      title: text("Actions"),
      key: "actions",
      valueType: "option",
      fixed: "right",
      align: "right",
      render: (_, row) => {
        const canEditRow = Boolean(row.priceAdjustmentId) || canCustomizePriceAdjustments;
        return [
          <Button key="edit" type="link" size="small" icon={<EditOutlined />} disabled={!canEditRow} onClick={() => openEdit(row)}>
            {text("Edit")}
          </Button>,
          <Button key="del" type="link" size="small" danger disabled={Boolean(financeOperation.entry)} icon={<DeleteOutlined />} onClick={() => deleteRow(row)}>
            {text("Delete")}
          </Button>
        ];
      }
    };
    return { columnDefinitions, allColumnsMap, actionsColumn };
  }, [canCustomizePriceAdjustments, deleteRow, financeOperation.entry, formatAdjustmentValue, openEdit, text, units]);

  const contractPackageOptions = React.useMemo(() => {
    const packageNameById = new Map(contractPackages.map((contractPackage) => [contractPackage.id, contractPackage.planName]));
    const packageIdsWithContracts = Array.from(
      new Set(individualContracts.map((contract) => contract.sourceContractPackageId).filter((id): id is string => Boolean(id)))
    );
    const options: Array<{ value: string; label: string }> = packageIdsWithContracts.map((packageId) => {
      const fallbackContract = individualContracts.find((contract) => contract.sourceContractPackageId === packageId);
      const fallbackLabel = fallbackContract?.name
        ? `${text("Contract package")} (${fallbackContract.name})`
        : `${text("Contract package")} ${packageId}`;
      return {
        value: packageId,
        label: packageNameById.get(packageId) || fallbackLabel
      };
    });
    if (individualContracts.some((contract) => !contract.sourceContractPackageId)) {
      options.push({ value: CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION, label: text("Contracts without contract package") });
    }
    if (edit) {
      const contract = individualContracts.find((x) => x.id === edit.contractId);
      const key = getContractPackageSelectId(contract);
      if (key && !options.some((option) => option.value === key)) {
        const label =
          key === CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION
            ? text("Contracts without contract package")
            : contractPackages.find((contractPackage) => contractPackage.id === key)?.planName || text("Contract package");
        options.push({ value: key, label });
      }
    }
    return options;
  }, [contractPackages, edit, individualContracts, text]);

  const contractsForSelectedPackage = React.useMemo(() => {
    if (!selectedContractPackageId) return [];
    if (selectedContractPackageId === CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION) {
      return individualContracts.filter((contract) => !contract.sourceContractPackageId);
    }
    return individualContracts.filter((contract) => contract.sourceContractPackageId === selectedContractPackageId);
  }, [individualContracts, selectedContractPackageId]);

  const applyPackageSelection = React.useCallback(
    (packageId: string) => {
      if (edit) return;
      setSelectedContractPackageId(packageId || null);
      const contracts =
        packageId === CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION
          ? individualContracts.filter((contract) => !contract.sourceContractPackageId)
          : individualContracts.filter((contract) => contract.sourceContractPackageId === packageId);
      const primary = contracts.find((contract) => contract.isPrimary) ?? contracts[0];
      if (primary) {
        const nextStart = getNextPriceAdjustmentPeriodStart(primary);
        form.setFieldsValue({
          contractId: primary.id,
          ...(nextStart ? { startDate: dayjs(nextStart) } : {})
        });
      } else {
        form.setFieldsValue({ contractId: null });
      }
    },
    [edit, form, individualContracts]
  );

  const applyContractSelection = React.useCallback(
    (contractId: string) => {
      if (edit) return;
      const contract = individualContracts.find((item) => item.id === contractId);
      if (!contract) return;
      const nextStart = getNextPriceAdjustmentPeriodStart(contract);
      if (nextStart) form.setFieldValue("startDate", dayjs(nextStart));
    },
    [edit, form, individualContracts]
  );

  React.useEffect(() => {
    if (!open || !edit || individualContracts.length === 0) return;
    const currentPackage = form.getFieldValue("contractPackageId");
    if (currentPackage != null && currentPackage !== "") return;
    const contract = individualContracts.find((x) => x.id === edit.contractId);
    const packageId = getContractPackageSelectId(contract);
    if (packageId) {
      setSelectedContractPackageId(packageId);
      form.setFieldValue("contractPackageId", packageId);
    }
  }, [edit, form, individualContracts, open]);

  const priceAdjustmentOptions = React.useMemo(
    () =>
      priceAdjustments.map((definition) => ({
        value: definition.id,
        label: `${definition.name} (${formatAdjustmentValue(
          Number(definition.adjustmentValue || 0),
          Number(definition.adjustmentType || PRICE_ADJUSTMENT_TYPE_PERCENTAGE)
        )}, ${billingCycleLabel(normalizePriceAdjustmentCycleUnit(definition.cycleUnit), text)})`
      })),
    [formatAdjustmentValue, priceAdjustments, text]
  );

  const request = React.useCallback(async () => {
    const list =
      (await api
        .get<ContractPriceAdjustmentRow[]>(`/api/v1/contract-price-adjustments?personId=${encodeURIComponent(personId)}`)) || [];
    const rows = [...list].sort(comparePriceAdjustmentsNewestFirst);
    return { data: rows, success: true, total: rows.length };
  }, [api, personId]);

  const save = React.useCallback(async () => {
    if (targetsLoading) return;
    try {
      const values = await form.validateFields();
      const startIso = formatDateOnlyIso(values.startDate);
      if (!startIso) throw new Error(text("Start date is required"));
      const endIso = formatDateOnlyIso(values.endDate);
      if (endIso && startIso >= endIso) throw new Error(text("End date must be after start date"));
      if (!values.contractId) throw new Error(text("Contract is required"));
      if (!canCustomizePriceAdjustments && !values.priceAdjustmentId) {
        throw new Error(text("Price adjustment is required"));
      }
      const manualName = String(values.manualAdjustmentName || "").trim();
      if (canCustomizePriceAdjustments && !manualName) {
        throw new Error(text("Manual price adjustment name is required"));
      }
      if (canCustomizePriceAdjustments && values.manualAdjustmentValue == null) {
        throw new Error(text("Adjustment value is required"));
      }
      const manualType = Number(values.manualAdjustmentType || PRICE_ADJUSTMENT_TYPE_PERCENTAGE);
      const manualValue = Number(values.manualAdjustmentValue || 0);
      if (canCustomizePriceAdjustments && manualType === PRICE_ADJUSTMENT_TYPE_SET_PRICE && manualValue < 0) {
        throw new Error(text("Target price cannot be negative"));
      }
      if (canCustomizePriceAdjustments && manualType === PRICE_ADJUSTMENT_TYPE_PERCENTAGE && manualValue < -MAX_PERCENTAGE_REDUCTION) {
        throw new Error(text("Percentage reduction cannot exceed 100%"));
      }

      setSaving(true);
      try {
        const dto = {
          contractId: values.contractId,
          priceAdjustmentId: values.priceAdjustmentId || null,
          manualAdjustmentName: canCustomizePriceAdjustments ? manualName : null,
          manualAdjustmentType: canCustomizePriceAdjustments ? manualType : null,
          manualAdjustmentValue: canCustomizePriceAdjustments ? manualValue : null,
          manualAdjustmentCycleUnit: canCustomizePriceAdjustments
            ? normalizePriceAdjustmentCycleUnit(values.manualAdjustmentCycleUnit)
            : null,
          startDate: startIso,
          endDate: endIso || null,
          note: values.note || null
        };
        await financeOperation.execute(async (committed) => {
          if (edit) {
            await api.put(`/api/v1/contract-price-adjustments/${encodeURIComponent(edit.id)}`, dto);
          } else {
            await api.post("/api/v1/contract-price-adjustments", dto);
          }
          committed();
          message.success(text("Saved."));
          setOpen(false);
          setEdit(null);
        }, async () => {
          await queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.priceAdjustments(personId) });
          reloadDependents();
        });
      } finally {
        setSaving(false);
      }
    } catch (error: unknown) {
      // Bei einer Formularprüfung steht die sprechende Meldung im ersten beanstandeten Feld.
      const validationMessage = isFormValidationError(error)
        ? String((error as { errorFields: Array<{ errors?: unknown[] }> }).errorFields[0]?.errors?.[0] ?? "")
        : "";
      const errorMessage = validationMessage || errorMessageOf(error);
      if (errorMessage && typeof errorMessage === "string") {
        message.error(errorMessage);
      }
    }
  }, [api, canCustomizePriceAdjustments, edit, form, message, personId, queryClient, reloadDependents, text, targetsLoading]);

  return {
    actionRef,
    dirty,
    open,
    saving,
    targetsLoading,
    edit,
    form,
    individualContracts,
    selectedContractPackageId,
    contractPackageOptions,
    contractsForSelectedPackage,
    priceAdjustmentOptions,
    applyPriceAdjustmentDefinition,
    listConfig,
    request,
    openCreate,
    openEdit,
    close,
    save,
    applyPackageSelection,
    applyContractSelection,
    isValidPeriodStart: isValidPriceAdjustmentPeriodStart
  };
}
